import {Component, ElementRef, Inject, Input, OnInit, ViewChild, OnDestroy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {EditingAction, IImageWrapper} from '../../../models/image-wrapper';
import {ImageCroppedEvent, ImageTransform} from 'ngx-image-cropper';
import {SessionServiceService} from '../../services/session-service.service';
import {DataUrl} from '../../../utils/utils';
import { UI_ACTION_CONSTANTS } from '../../services/logger/constants';
import { LoggerService } from '../../services/logger/logger.service';
import {HttpClient} from '@angular/common/http';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import 'rxjs/add/operator/catch';
/**
 * Props to Freaky Jolly @ https://www.freakyjolly.com/angular-image-uploader-with-cropper-and-preview-tutorial-with-example/
 */


export interface IDialogEditImageComponentOptions {
  image: IImageWrapper;
  targetAspectRatio: number;
  useBase64DataUrl?: boolean;
}

export interface IDialogEditImageReturnParams {
  base64: DataUrl;
  dimension: {
    width: number;
    height: number;
    ratio: number;
  };
  action: EditingAction[];
}

export interface ImageFilter {
  name: string;
  style: string;
}

export interface ImageAdjustments {
  brightness: number;
  contrast: number;
  saturation: number;
}

export interface TextOverlay {
  text: string;
  x: number;
  y: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  enabled: boolean;
}

@Component({
  selector: 'app-dialog-edit-image',
  templateUrl: './dialog-edit-image.component.html',
  styleUrls: ['./dialog-edit-image.component.scss']
})
export class DialogEditImageComponent implements OnInit, OnDestroy {
  @ViewChild('Image', {static: false}) refImage: ElementRef;
  // Reference to the canvas element for rendering the final image
  @ViewChild('canvas', {static: false}) canvas: ElementRef;

  public selectedImageFromExternalApi$: Observable<IImageWrapper>;

  /**
   * When 'true' is passed, then the image has already been edited before and should have the corresponding field on the IImageWrapper object
   */
  @Input('dirty') _passedImageIsAlreadyEdited: boolean;

  public imgCssClass: string = null;
  public imgSrc: {url: string; base64: DataUrl} = {
    url: null,
    base64: null
  };
  public imgTransform: ImageTransform = {
    scale: 1,
    rotate: 0,
    flipH: false,
    flipV: false
  };
  public canvasRotation = 0;
  public cropperAspectRatio = 1;
  public maxImageDimensions = {
    width: window.innerWidth * 0.6,
    height: window.innerHeight * 0.7
  };
  
  // Preview image URL for filters
  public previewImageUrl: string = '';
  
  // Store the cropped image event for final processing
  private latestCroppedEvent: ImageCroppedEvent = null;
  private initialCropDone: boolean = false;
  
  // Track if the image is ready for editing
  public isImageReady: boolean = false;
  public isProcessing: boolean = false;
  
  // Track if this is from AI generation
  private isFromAiGeneration: boolean = false;

  // New properties for enhanced editing
  public currentFilter: string = 'Normal';
  public availableFilters: ImageFilter[] = [
    { name: 'Normal', style: 'none' },
    { name: 'Grayscale', style: 'grayscale(100%)' },
    { name: 'Sepia', style: 'sepia(100%)' },
    { name: 'Vintage', style: 'sepia(80%) brightness(90%)' },
    { name: 'Dramatic', style: 'contrast(120%) brightness(90%)' },
    { name: 'Vivid', style: 'saturate(150%) contrast(110%)' }
  ];
  
  public adjustments: ImageAdjustments = {
    brightness: 0,
    contrast: 0,
    saturation: 0
  };
  
  public textOverlay: TextOverlay = {
    text: '',
    x: 50,
    y: 50,
    fontSize: 24,
    fontFamily: 'Arial',
    color: '#ffffff',
    enabled: true
  };
  
  // Store original state for reset functionality
  private originalCanvasRotation = 0;
  private originalTransform: ImageTransform = {
    scale: 1,
    rotate: 0,
    flipH: false,
    flipV: false
  };

  imageState: IDialogEditImageReturnParams = {
    base64: null,
    dimension: {
      height: null,
      width: null,
      ratio: null
    },
    action: []
  };

  imageChangedEvent: any = '';
  croppedImage: any = '';

  private hasInitializedCrop: boolean = false;

  constructor(
    private _dialogEditImage: MatDialogRef<DialogEditImageComponent>,
    @Inject(MAT_DIALOG_DATA) private _dataPassedToDialog: IDialogEditImageComponentOptions,
    private _session: SessionServiceService,
    private _logger: LoggerService,
    private http: HttpClient,
    public dialogRef: MatDialogRef<DialogEditImageComponent>
  ) {
    this._session.awaitAsyncAction('edit-dialog-loading-image', "Preparing image cropping...");
    
    // Update max dimensions on window resize
    window.addEventListener('resize', this.updateMaxDimensions.bind(this));
  }

  ngOnInit() {
    const _data = this._dataPassedToDialog;
    
    // Check if this is from AI generation
    this.isFromAiGeneration = _data.useBase64DataUrl === true;
    console.log('Edit dialog initialized:', {
      isFromAiGeneration: this.isFromAiGeneration,
      hasBase64: !!_data.image.base64,
      hasSource: !!_data.image.source,
      imageType: this.isFromAiGeneration ? 'AI Generated' : 'External Source',
      dimensions: _data.image.dimension
    });
    
    // Reset state
    this.isImageReady = false;
    this.isProcessing = false;
    this.hasInitializedCrop = false;
    this.initialCropDone = false;
    
    if (_data.useBase64DataUrl && _data.image.base64) {
      console.log('Loading AI-generated image from base64');
      this.imgSrc.base64 = _data.image.base64;
      this.previewImageUrl = this.imgSrc.base64.toString();
      console.log('Base64 image data loaded:', {
        dataLength: this.imgSrc.base64.toString().length,
        previewSet: !!this.previewImageUrl
      });
      this.updateMaxDimensions();
      
      // For AI-generated images, we need to wait a bit before marking as initialized
      setTimeout(() => {
        this.hasInitializedCrop = true;
        console.log('AI image initialization complete');
      }, 300);
    } else {
      console.log('Loading image from external URL:', _data.image.source);
      this.http.get(_data.image.source, {responseType: 'blob'}).toPromise().then(async (d) => {
        console.log('Blob received from external source:', {
          size: d.size,
          type: d.type
        });
        const fileReader = new FileReader();
        fileReader.onloadend = () => {
          const base64data = fileReader.result.toString().split(',')[1];
          this.imgSrc.base64 = new DataUrl(d.type, base64data, true);
          this.previewImageUrl = this.imgSrc.base64.toString();
          console.log('External image loaded and converted:', {
            dataLength: base64data.length,
            type: d.type,
            previewSet: !!this.previewImageUrl
          });
          this.updateMaxDimensions();
          this.hasInitializedCrop = true;
        }
        fileReader.readAsDataURL(d);
      }).catch((e) => {
        console.error('Failed to load external image:', e);
        this._session.doneAsyncAction('edit-dialog-loading-image');
        setTimeout(() => {
          this._session.alert("Loading image failed.", "Please try again later or choose a different image.");
        }, 500);
        this.dialogRef.close();
      });
    }

    // Set image class based on ratio
    if (this._dataPassedToDialog.image.dimension.ratio >= 1) {
      this.imgCssClass = 'image-ratio-at-least-one';
    } else {
      this.imgCssClass = 'image-ratio-smaller-than-one';
    }
    console.log('Image ratio class set:', {
      ratio: this._dataPassedToDialog.image.dimension.ratio,
      class: this.imgCssClass
    });

    if (this._dataPassedToDialog?.targetAspectRatio) {
      this.cropperAspectRatio = this._dataPassedToDialog.targetAspectRatio;
      console.log('Target aspect ratio set:', this.cropperAspectRatio);
    }
    
    this.originalTransform = {...this.imgTransform};
    this.originalCanvasRotation = this.canvasRotation;
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.updateMaxDimensions.bind(this));
  }

  private updateMaxDimensions() {
    this.maxImageDimensions = {
      width: window.innerWidth * 0.6,
      height: window.innerHeight * 0.7
    };
  }

  /**
   * Fired when the cropper is done loading the image
   */
  public imageLoaded(): void {
    console.log('Image loaded in cropper:', {
      isFromAiGeneration: this.isFromAiGeneration,
      hasBase64: !!this.imgSrc.base64,
      hasInitializedCrop: this.hasInitializedCrop,
      imageState: {
        width: this.imageState.dimension.width,
        height: this.imageState.dimension.height,
        ratio: this.imageState.dimension.ratio
      }
    });
    
    this._session.doneAsyncAction('edit-dialog-loading-image');
    
    if (this.isFromAiGeneration && !this.hasInitializedCrop) {
      console.log('Processing AI-generated image load');
      this.hasInitializedCrop = true;
      // For AI-generated images, we need to ensure we get an initial crop
      setTimeout(() => {
        if (!this.initialCropDone) {
          console.log('Forcing initial crop for AI image');
          this.isImageReady = true;
          // Try to get the cropper instance
          const cropperElement = document.querySelector('image-cropper');
          console.log('Cropper element found:', !!cropperElement);
          if (cropperElement) {
            try {
              // Create and dispatch a crop event
              const cropEvent = new CustomEvent('imageCropped', {
                bubbles: true,
                cancelable: true,
                detail: {
                  base64: this.imgSrc.base64.toString(),
                  width: this.imageState.dimension.width,
                  height: this.imageState.dimension.height
                }
              });
              cropperElement.dispatchEvent(cropEvent);
              console.log('Manual crop event dispatched');
            } catch (error) {
              console.error('Error dispatching crop event:', error);
            }
          }
        }
      }, 300);
    } else if (!this.isFromAiGeneration) {
      console.log('Processing external image load - marking as ready');
      this.isImageReady = true;
    }
  }

  /**
   * Fired when image loading fails
   */
  public loadImageFailed(): void {
    console.error('Image loading failed');
    this._session.alert("Loading image failed.", "Please try again later or choose a different image.")
    this._session.doneAsyncAction('edit-dialog-loading-image');
    this.isImageReady = false;
    this.initialCropDone = false;
  }

  /**
   * Fired when the image is cropped
   */
  public imageCropped(event: ImageCroppedEvent): void {
    // Prevent duplicate crop events
    if (this.isFromAiGeneration && !this.hasInitializedCrop) {
      console.log('Ignoring crop event before initialization');
      return;
    }

    console.log('Image crop event received:', {
      isFromAiGeneration: this.isFromAiGeneration,
      hasInitializedCrop: this.hasInitializedCrop,
      eventDetails: {
        width: event.width,
        height: event.height,
        cropperPosition: event.cropperPosition,
        imagePosition: event.imagePosition,
        hasBase64: !!event.base64
      },
      currentState: {
        isReady: this.isImageReady,
        initialCropDone: this.initialCropDone,
        isProcessing: this.isProcessing
      }
    });

    // Store the latest cropped event
    this.latestCroppedEvent = event;
    
    // Update image state
    this.imageState.base64 = DataUrl.fromString(event.base64);
    this.imageState.dimension.height = event.height;
    this.imageState.dimension.width = event.width;
    this.imageState.dimension.ratio = event.width / event.height;
    
    // Mark as ready
    this.isImageReady = true;
    this.initialCropDone = true;
    
    console.log('Image state updated after crop:', {
      dimensions: this.imageState.dimension,
      isReady: this.isImageReady,
      initialCropDone: this.initialCropDone,
      hasInitializedCrop: this.hasInitializedCrop
    });
  }

  public cropperReady(): void {
    // Prevent duplicate ready events
    if (this.isFromAiGeneration && !this.hasInitializedCrop) {
      console.log('Waiting for AI image initialization before processing cropper ready');
      return;
    }

    console.log('Cropper ready event:', {
      isFromAiGeneration: this.isFromAiGeneration,
      hasInitializedCrop: this.hasInitializedCrop,
      currentState: {
        isReady: this.isImageReady,
        initialCropDone: this.initialCropDone,
        hasLatestCrop: !!this.latestCroppedEvent
      }
    });

    if (this.isFromAiGeneration && !this.initialCropDone) {
      console.log('AI image cropper ready - waiting for initial crop');
      // No need to dispatch event here, it's handled in imageLoaded
      this.isImageReady = true;
    } else {
      console.log('External image cropper ready - marking as ready');
      this.isImageReady = true;
    }
  }

  /**
   * Rotate the image 90 degrees clockwise
   */
  public rotate90Clockwise(): void {
    this.canvasRotation = (this.canvasRotation + 1) % 4;
    this.updateActions();
  }

  /**
   * Rotate the image 90 degrees counter-clockwise
   */
  public rotate90CounterClockwise(): void {
    this.canvasRotation = (this.canvasRotation - 1) % 4;
    this.updateActions();
  }
  
  /**
   * Apply a filter to the image
   */
  public applyFilter(filterName: string): void {
    this.currentFilter = filterName;
    this.updateActions();
  }
  
  /**
   * Handle changes to brightness, contrast, etc
   */
  public onAdjustmentChange(): void {
    this.updateActions();
  }
  
  /**
   * Reset all adjustments to default values
   */
  public resetAdjustments(): void {
    this.adjustments = {
      brightness: 0,
      contrast: 0,
      saturation: 0
    };
    this.updateActions();
  }
  
  /**
   * Update text overlay properties
   */
  public updateTextOverlay(): void {
    // Two-way binding handles the updates
    this.updateActions();
  }
  
  /**
   * Reset all changes to the original state
   */
  public resetAllChanges(): void {
    this.imgTransform = {...this.originalTransform};
    this.canvasRotation = this.originalCanvasRotation;
    this.currentFilter = 'Normal';
    this.resetAdjustments();
    this.textOverlay.text = '';
    this.updateActions();
  }
  
  /**
   * Get the CSS filter string for the currently applied filters and adjustments
   */
  public getCurrentFilters(): string {
    let filter = '';
    
    // First apply the named filter
    const selectedFilter = this.availableFilters.find(f => f.name === this.currentFilter);
    if (selectedFilter && selectedFilter.style !== 'none') {
      filter += selectedFilter.style;
    }
    
    // Then apply the adjustments
    if (this.adjustments.brightness !== 0) {
      filter += ` brightness(${100 + this.adjustments.brightness}%)`;
    }
    
    if (this.adjustments.contrast !== 0) {
      filter += ` contrast(${100 + this.adjustments.contrast}%)`;
    }
    
    if (this.adjustments.saturation !== 0) {
      filter += ` saturate(${100 + this.adjustments.saturation}%)`;
    }
    
    return filter.trim();
  }
  
  /**
   * Update the list of actions that have been applied to the image
   */
  private updateActions(): void {
    // Reset actions list
    this.imageState.action = [];
    
    // Add actions based on current state
    if (this.canvasRotation !== 0) {
      this.imageState.action.push('rotated');
    }
    
    this.imageState.action.push('cropped');
    
    if (this.currentFilter !== 'Normal') {
      this.imageState.action.push('filtered');
    }
    
    if (this.adjustments.brightness !== 0 || this.adjustments.contrast !== 0 || 
        this.adjustments.saturation !== 0) {
      this.imageState.action.push('adjusted');
    }
    
    if (this.textOverlay.text.trim()) {
      this.imageState.action.push('text-added');
    }
  }

  /**
   * Apply all edits to the image data (filters, text, etc.)
   * This creates a new canvas, draws the cropped image with filters applied,
   * then draws text on top if needed.
   */
  private applyEditsToImage(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.latestCroppedEvent) {
        reject('No cropped image available');
        return;
      }
      
      // Create an image element to load the cropped image
      const img = new Image();
      img.onload = () => {
        // Create a canvas to apply filters and text
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        // Draw the image
        ctx.drawImage(img, 0, 0);
        
        // Apply filters using CSS filter
        const filterString = this.getCurrentFilters();
        if (filterString) {
          ctx.filter = filterString;
          // Redraw the image with the filter applied
          ctx.drawImage(img, 0, 0);
          // Reset filter for text
          ctx.filter = 'none';
        }
        
        // Add text if specified
        if (this.textOverlay.text.trim()) {
          ctx.font = `${this.textOverlay.fontSize}px ${this.textOverlay.fontFamily}`;
          ctx.fillStyle = this.textOverlay.color;
          ctx.textBaseline = 'middle';
          
          // Calculate position based on percentages
          const x = (this.textOverlay.x / 100) * canvas.width;
          const y = (this.textOverlay.y / 100) * canvas.height;
          
          // Add text shadow for better visibility
          ctx.shadowColor = 'rgba(0,0,0,0.7)';
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Draw the text
          ctx.fillText(this.textOverlay.text, x, y);
          
          // Reset shadow
          ctx.shadowColor = 'transparent';
        }
        
        // Get the final image as base64
        const finalImageBase64 = canvas.toDataURL('image/png');
        resolve(finalImageBase64);
      };
      
      img.onerror = () => {
        reject('Error loading cropped image');
      };
      
      // Set the source of the image to the cropped image
      img.src = this.latestCroppedEvent.base64;
    });
  }

  /**
   * Process edits and close the dialog
   */
  public async processEditAndCloseDialog(): Promise<void> {
    console.log('Process edit requested:', {
      isFromAiGeneration: this.isFromAiGeneration,
      hasInitializedCrop: this.hasInitializedCrop,
      currentState: {
        isReady: this.isImageReady,
        initialCropDone: this.initialCropDone,
        isProcessing: this.isProcessing,
        hasLatestCrop: !!this.latestCroppedEvent
      }
    });

    if (this.isProcessing) {
      console.log('Already processing edits - ignoring duplicate click');
      return;
    }

    if (!this.isImageReady || !this.latestCroppedEvent || !this.initialCropDone || !this.hasInitializedCrop) {
      console.log('Image not ready for processing:', {
        isImageReady: this.isImageReady,
        hasLatestCropEvent: !!this.latestCroppedEvent,
        initialCropDone: this.initialCropDone,
        hasInitializedCrop: this.hasInitializedCrop
      });
      this._session.alert(
        "Please wait",
        "The image is still being prepared. Please try again in a moment."
      );
      return;
    }

    try {
      this.isProcessing = true;
      console.log('Starting to process edits');
      this._session.awaitAsyncAction('edit-dialog-applying-changes', "Applying image edits...");
      
      // Add a small delay to ensure all state updates are complete
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Apply edits
      const editedImageBase64 = await this.applyEditsToImage();
      console.log('Edits applied successfully:', {
        hasEditedImage: !!editedImageBase64
      });
      
      if (!editedImageBase64) {
        throw new Error('Failed to apply edits to image');
      }

      // Update state
      this.imageState.base64 = new DataUrl(
        'image/png',
        editedImageBase64.split(',')[1],
        true
      );

      if (this.latestCroppedEvent) {
        this.imageState.dimension = {
          width: this.latestCroppedEvent.width,
          height: this.latestCroppedEvent.height,
          ratio: this.latestCroppedEvent.width / this.latestCroppedEvent.height
        };
      }

      // Log action
      this._logger.log(UI_ACTION_CONSTANTS.IMAGE_EDITED, {
        transform: this.imgTransform,
        rotation: this.canvasRotation,
        filter: this.currentFilter,
        adjustments: this.adjustments,
        hasTextOverlay: !!this.textOverlay.text.trim(),
        isFromAiGeneration: this.isFromAiGeneration,
        dimensions: this.imageState.dimension
      });

      console.log('Edit processing complete, preparing to close dialog');
      this._session.doneAsyncAction('edit-dialog-applying-changes');
      
      // Ensure async operations complete
      await new Promise(resolve => setTimeout(resolve, 300));
      
      console.log('Closing dialog with final state:', {
        dimensions: this.imageState.dimension,
        hasBase64: !!this.imageState.base64,
        actions: this.imageState.action
      });
      
      this._dialogEditImage.close(this.imageState);
    } catch (error) {
      console.error('Error processing edits:', error);
      this._session.doneAsyncAction('edit-dialog-applying-changes');
      this._session.alert(
        "Edit Error",
        "There was a problem applying your edits to the image. Please try again."
      );
    } finally {
      this.isProcessing = false;
    }
  }
}
